import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { announcementService } from '../../../services/announcementService';

const AnnouncementManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'low'
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await announcementService?.getAnnouncements();
      setAnnouncements(data || []);
    } catch (err) {
      console.error('Error loading announcements:', err);
      setError(err?.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.title?.trim() || !formData?.message?.trim()) {
      setError('Title and message are required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      if (editingId) {
        await announcementService?.updateAnnouncement(editingId, formData);
      } else {
        await announcementService?.createAnnouncement(formData);
      }

      setFormData({ title: '', message: '', priority: 'low' });
      setIsFormOpen(false);
      setEditingId(null);
      await loadAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      setError(err?.message || 'Failed to save announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement?.title,
      message: announcement?.message,
      priority: announcement?.priority
    });
    setEditingId(announcement?.id);
    setIsFormOpen(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await announcementService?.deleteAnnouncement(id);
      await loadAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError(err?.message || 'Failed to delete announcement');
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', message: '', priority: 'low' });
    setIsFormOpen(false);
    setEditingId(null);
    setError('');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Announcements</h2>
          <p className="text-muted-foreground mt-1">Manage platform-wide announcements for students</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          onClick={() => setIsFormOpen(true)}
        >
          New Announcement
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error rounded-lg p-4">
          <p className="font-medium">{error}</p>
        </div>
      )}

      {isFormOpen && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {editingId ? 'Edit Announcement' : 'Create New Announcement'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Title"
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              placeholder="Enter announcement title"
              required
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message
              </label>
              <textarea
                value={formData?.message}
                onChange={(e) => setFormData({ ...formData, message: e?.target?.value })}
                placeholder="Enter announcement message"
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <Select
              label="Priority"
              options={priorityOptions}
              value={formData?.priority}
              onChange={(value) => setFormData({ ...formData, priority: value })}
            />
            <div className="flex gap-3">
              <Button
                type="submit"
                variant="default"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {announcements?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Megaphone" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">No announcements yet. Create your first announcement.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {announcements?.map((announcement) => (
              <div key={announcement?.id} className="p-4 hover:bg-muted transition-smooth">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-semibold text-foreground">
                        {announcement?.title}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(announcement?.priority)}`}>
                        {announcement?.priority}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {announcement?.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(announcement?.timestamp)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="p-2 hover:bg-primary/10 rounded-lg transition-smooth"
                      title="Edit"
                    >
                      <Icon name="Edit" size={18} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement?.id)}
                      className="p-2 hover:bg-error/10 rounded-lg transition-smooth"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={18} color="var(--color-error)" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementManager;